import { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import {
  Loader2, ArrowLeft, MapPin, CalendarDays, Lock, MessageCircle, BadgeCheck, Plane, ShieldCheck,
} from "lucide-react";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { CATEGORIES_ABONNEMENT as RUBRIQUES } from "@/lib/categories";
import { InvitationPrivee } from "@/components/InvitationPrivee";
import { CadeauAnnonce } from "@/components/CadeauAnnonce";
import { formatDate, formatFCFA, getUser } from "@/lib/user";
import { versionMedia } from "@/lib/apercu";
import { estNouvelleAnnonce, BadgeNouveau } from "@/lib/annonces";
import { CarrouselAnnonce } from "@/components/CarrouselAnnonce";
import { ContactAnnonceur } from "@/components/ContactAnnonceur";
import { ReactionsLove } from "@/components/ReactionsLove";
import { ProfileStatsBar } from "@/components/ProfileStatsBar";
import { PartageAnnonce } from "@/components/PartageAnnonce";
import { PastilleEnLigne } from "@/components/PastilleEnLigne";
import { useSeo, useDonneesStructurees } from "@/lib/seo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const mediaUrl = (u) => {
  if (!u?.startsWith("/api")) return u;
  const user = getUser();
  return `${process.env.REACT_APP_BACKEND_URL}${u}?v=${versionMedia()}${user ? `&viewer_id=${user.id}` : ""}`;
};

/** Page dédiée d'une annonce : texte intégral + galerie interactive. */
export default function AnnonceDetail() {
  const { annonceId } = useParams();
  const navigate = useNavigate();
  const [annonce, setAnnonce] = useState(null);
  const [erreur, setErreur] = useState(null);
  const [chargement, setChargement] = useState(true);
  const [prixDes, setPrixDes] = useState(null);

  useEffect(() => {
    const user = getUser();
    axios
      .get(`${API}/annonces/${annonceId}/detail`, { params: { viewer_id: user?.id } })
      .then((r) => setAnnonce(r.data))
      .catch((err) =>
        setErreur(err.response?.data?.detail?.message || err.response?.data?.detail || "Annonce introuvable."),
      )
      .finally(() => setChargement(false));
  }, [annonceId]);

  // Prix d'entrée de la rubrique, affiché sur le cadenas pour déclencher l'achat.
  useEffect(() => {
    if (!annonce?.categorie) return;
    axios
      .get(`${API}/pricing`)
      .then((r) => setPrixDes(r.data?.[annonce.categorie]?.base?.decouverte || null))
      .catch(() => {});
  }, [annonce?.categorie]);

  // Référencement : chaque annonce a son propre titre, sa description et son aperçu.
  const nomRubrique = annonce ? RUBRIQUES[annonce.categorie]?.name || annonce.categorie : "";
  const lieu = annonce?.annonceur?.city || annonce?.air?.ville || "Côte d'Ivoire";
  const nomAnnonceur = annonce?.annonceur?.firstname || "";
  useSeo({
    titre: annonce
      ? (nomAnnonceur && nomAnnonceur !== "FodindinCa"
          ? `${nomAnnonceur} — ${nomRubrique} à ${lieu}`
          : `${nomRubrique} à ${lieu} — annonce`)
      : null,
    description: annonce
      ? (annonce.texte || "").slice(0, 155) ||
        `Annonce ${nomRubrique} publiée à ${lieu} sur FodindinCa.`
      : null,
    image: annonce?.photos?.[0]?.url || null,
    chemin: `/annonces/${annonceId}`,
  });
  useDonneesStructurees(
    annonce
      ? {
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "FodindinCa", item: "https://fodindinca.com/" },
            { "@type": "ListItem", position: 2, name: nomRubrique,
              item: `https://fodindinca.com/categorie/${annonce.categorie}` },
            { "@type": "ListItem", position: 3, name: annonce.annonceur?.firstname,
              item: `https://fodindinca.com/annonces/${annonceId}` },
          ],
        }
      : null,
  );

  if (chargement) {
    return (
      <div className="min-h-screen bg-[#FAF8F5]">
        <Navbar />
        <div className="flex h-[60vh] items-center justify-center" data-testid="detail-chargement">
          <Loader2 className="animate-spin text-stone-400" size={28} />
        </div>
      </div>
    );
  }

  if (erreur || !annonce) {
    return (
      <div className="min-h-screen bg-[#FAF8F5]">
        <Navbar />
        <main className="mx-auto max-w-3xl px-4 py-20 text-center" data-testid="detail-erreur">
          <Lock size={30} className="mx-auto text-stone-400" />
          <h1 className="font-heading mt-4 text-2xl font-bold text-stone-900">Annonce indisponible</h1>
          <p className="mt-2 text-sm text-stone-600">{erreur}</p>
          <Link
            to="/annonces-consulter"
            data-testid="detail-retour-liste"
            className="mt-6 inline-flex rounded-full bg-[#12100E] px-6 py-3 text-sm font-bold text-white"
          >
            Voir les autres annonces
          </Link>
        </main>
        <Footer />
      </div>
    );
  }

  const rubrique = RUBRIQUES[annonce.categorie] || {};
  const couleur = rubrique.color || "#12100E";
  const medias = [
    ...(annonce.photos || []).map((p) => ({ ...p, type: "photo", url: mediaUrl(p.url) })),
    // Photos réservées aux abonnés : le serveur n'envoie qu'un aperçu minuscule.
    ...(annonce.photos_verrouillees || []).map((p) => ({
      type: "photo",
      url: mediaUrl(p.apercu),
      verrouille: true,
    })),
    ...(annonce.video ? [{ ...annonce.video, type: "video", url: mediaUrl(annonce.video.url) }] : []),
    // Vidéo réservée : le serveur ne livre que les premières secondes.
    ...(annonce.video_verrouillee
      ? [{
          type: "video",
          url: mediaUrl(annonce.video_verrouillee.apercu),
          verrouille: true,
          secondesLibres: annonce.video_verrouillee.secondes_libres,
        }]
      : []),
  ];
  const lienListe = annonce.categorie === "air" ? "/air-bengue" : `/categorie/${annonce.categorie}/annonces`;
  const air = annonce.air;

  return (
    <div className="min-h-screen bg-[#FAF8F5]">
      <Navbar />

      <header className="border-b border-[#E5DFD5]" style={{ backgroundColor: `${couleur}0f` }}>
        <div className="mx-auto max-w-5xl px-4 py-6 sm:px-6">
          <button
            onClick={() => (window.history.length > 1 ? navigate(-1) : navigate(lienListe))}
            data-testid="detail-retour"
            className="inline-flex items-center gap-2 text-sm font-semibold text-stone-600 transition-colors hover:text-stone-900"
          >
            <ArrowLeft size={16} /> Retour aux annonces
          </button>
          <div className="mt-4 flex flex-wrap items-center gap-3">
            <span
              className="inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-[11px] font-bold uppercase tracking-wider text-white"
              style={{ backgroundColor: couleur }}
              data-testid="detail-rubrique"
            >
              {annonce.categorie === "air" && <Plane size={12} />} {annonce.rubrique}
            </span>
            {estNouvelleAnnonce(annonce.date_debut) && (
              <BadgeNouveau testid="detail-badge-nouveau" />
            )}
            <span className="inline-flex items-center gap-1.5 text-xs text-stone-500">
              <CalendarDays size={13} /> Publiée le {formatDate(annonce.date_debut)}
            </span>
          </div>
          <h1 className="font-heading mt-3 text-3xl font-bold leading-tight text-stone-900 sm:text-4xl" data-testid="detail-titre">
            {air?.titre || `Annonce de ${annonce.annonceur?.firstname || "un membre"}`}
          </h1>
        </div>
      </header>

      <main className="mx-auto max-w-5xl px-4 py-8 sm:px-6">
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.45 }}>
          <CarrouselAnnonce
            medias={medias}
            couleur={couleur}
            floute={!annonce.photos_nettes}
            prixDes={prixDes}
            lienAbonnement="/abonnement?periode=decouverte"
            testid="detail-carrousel"
          />
        </motion.div>

        <div className="mt-8 grid gap-6 lg:grid-cols-[1.6fr_1fr]">
          <section className="space-y-6">
            <article
              className="rounded-2xl border border-[#E5DFD5] bg-white p-6"
              style={{ borderLeft: `4px solid ${couleur}` }}
            >
              <h2 className="text-base font-bold text-stone-900 md:text-lg">L'annonce en entier</h2>
              <p
                className="mt-3 whitespace-pre-line text-base leading-relaxed text-stone-700"
                data-testid="detail-texte"
              >
                {annonce.texte || "L'annonceur n'a pas ajouté de description."}
              </p>
              {annonce.texte && (
                <p className="mt-4 text-xs text-stone-400" data-testid="detail-longueur">
                  {annonce.texte.length} caractères
                </p>
              )}
            </article>

            {air && (
              <article className="rounded-2xl border border-[#E5DFD5] bg-white p-6" data-testid="detail-air">
                <h2 className="text-base font-bold text-stone-900 md:text-lg">Prestation à l'étranger</h2>
                <dl className="mt-3 grid gap-3 sm:grid-cols-2">
                  {[
                    ["Type", air.type_prestation],
                    ["Ville", air.ville],
                    ["Pays", air.pays],
                    ["Tarif", air.tarif ? formatFCFA(air.tarif) : null],
                    ["Disponibilité", air.disponibilite],
                  ]
                    .filter(([, v]) => v)
                    .map(([cle, valeur]) => (
                      <div key={cle}>
                        <dt className="text-xs uppercase tracking-wider text-stone-400">{cle}</dt>
                        <dd className="text-sm font-semibold text-stone-800">{valeur}</dd>
                      </div>
                    ))}
                </dl>
              </article>
            )}
          </section>

          <aside className="space-y-4">
            {["vip", "lago"].includes(annonce.categorie) && (
              <>
                <InvitationPrivee
                  annonceId={annonce.id}
                  nom={annonce.annonceur?.firstname || "cette personne"}
                  photo={annonce.photos?.[0]?.url ? mediaUrl(annonce.photos[0].url) : null}
                />
                <CadeauAnnonce
                  annonceId={annonce.id}
                  nom={annonce.annonceur?.firstname || "cette personne"}
                  photo={annonce.photos?.[0]?.url ? mediaUrl(annonce.photos[0].url) : null}
                />
              </>
            )}
            <div className="rounded-2xl border border-[#E5DFD5] bg-white p-6" data-testid="detail-annonceur">
              <div className="font-heading flex items-center gap-2 text-base font-bold text-stone-900">
                {annonce.annonceur?.firstname}
                {annonce.annonceur?.verified && <BadgeCheck size={16} className="text-[#0A5C36]" />}
                {annonce.annonceur?.certifie_visage && (
                  <span className="inline-flex items-center gap-1 rounded-full bg-[#0A5C36]/10 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-[#0A5C36]"
                    data-testid="detail-badge-certifie">
                    <ShieldCheck size={10} /> Profil certifié
                  </span>
                )}
                <PastilleEnLigne enLigne={annonce.annonceur?.en_ligne} testid="detail-presence" />
              </div>
              {annonce.annonceur?.city && (
                <div className="mt-1 flex items-center gap-1.5 text-xs text-stone-500">
                  <MapPin size={12} /> {annonce.annonceur.city}
                </div>
              )}

              {/* Statistiques du profil (LaGo, ClubVIP, Air-Bengue). */}
              {["lago", "vip", "air"].includes(annonce.categorie) && annonce.annonceur?.id && (
                <div className="mt-5">
                  <ProfileStatsBar
                    profileId={annonce.annonceur.id}
                    categorie={annonce.categorie}
                    viewerId={getUser()?.id}
                    /* Date d'enregistrement = dépôt de CETTE annonce (le compte annonceur
                       peut être bien plus ancien, ce qui donnait une date figée). */
                    inscritLe={annonce.date_debut}
                    onMessage={() => navigate(`/messages?to=${annonce.annonceur.id}`)}
                  />
                </div>
              )}

              <div className="mt-5 space-y-3">
                {/* FodindinLove : les 4 réactions sur la fiche du profil. */}
                {annonce.categorie === "love" && annonce.annonceur?.id && (
                  <ReactionsLove cibleId={annonce.annonceur.id} annonceId={annonce.id}
                    moiId={getUser()?.id} reactionInitiale={annonce.ma_reaction}
                    onMatch={() => navigate("/love?onglet=matchs")} />
                )}
                <ContactAnnonceur annonceId={annonce.id} couleur={couleur} />
                {annonce.refus && (
                  <p className="rounded-xl bg-[#E05638]/[0.07] px-3 py-2 text-xs text-stone-600" data-testid="detail-refus">
                    {annonce.refus.message}
                  </p>
                )}
                {annonce.annonceur?.id && (
                  <button
                    onClick={() => navigate(`/messages?to=${annonce.annonceur.id}`)}
                    data-testid="detail-message"
                    className="w-full inline-flex items-center justify-center gap-2 rounded-full border-2 py-2.5 text-sm font-semibold transition-colors"
                    style={{ borderColor: couleur, color: couleur }}
                  >
                    <MessageCircle size={15} /> Écrire un message
                  </button>
                )}
                {!annonce.numeros_debloques && (
                  <Link
                    to="/abonnement"
                    data-testid="detail-vers-abonnement"
                    className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-[#12100E] py-2.5 text-sm font-bold text-white"
                  >
                    <Lock size={14} /> Débloquer {annonce.rubrique}
                  </Link>
                )}
              </div>
            </div>

            <div className="flex items-center justify-between rounded-2xl border border-[#E5DFD5] bg-white px-5 py-4">
              <span className="text-xs text-stone-500">Partager cette annonce</span>
              <PartageAnnonce annonceId={annonce.id} reference={annonce.slug}
                titre={air?.titre || `Annonce de ${annonce.annonceur?.firstname}`} />
            </div>

            <Link
              to={lienListe}
              data-testid="detail-autres-annonces"
              className="block rounded-2xl border border-dashed border-[#E5DFD5] px-5 py-4 text-center text-sm font-semibold text-stone-600 transition-colors hover:border-stone-900 hover:text-stone-900"
            >
              Voir toutes les annonces {annonce.rubrique}
            </Link>
          </aside>
        </div>
      </main>
      <Footer />
    </div>
  );
}
